import { useState } from 'react';
import { ArrowRight, CheckCircle2, Loader2, PhoneCall } from 'lucide-react';
import { useRequestCallbackMutation } from '@/store/api/apiSlice';
import { parseErrorMessage } from '@/utils/parseErrorMessage';

interface CallbackFormProps {
    /** Plan the visitor clicked "talk to us" on, echoed to the backend for context. */
    selectedPlanId?: string | undefined;
}

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
// Nigerian mobile numbers, tolerating +234 / 0 prefixes and spacing.
const PHONE_PATTERN = /^(\+?234|0)?[789]\d{9}$/;

const FIELD_CLASS =
    'w-full rounded-xl border border-[#E4E1DA] bg-white px-3.5 py-3 text-[14px] text-[#15171C] placeholder:text-[#A8A399] transition-colors focus:border-[#FF6B35] focus:ring-2 focus:ring-[#FF6B35]/20 focus:outline-none';

/**
 * Lead capture for the one-pager.
 *
 * Rendered only inside the callback modal — every CTA on the page (header,
 * both plan buttons, the plan-details sheet) opens that one instance, so the
 * modal owns the heading and the "re: <plan>" chip and this component is just
 * the fields.
 */
export default function CallbackForm({ selectedPlanId }: CallbackFormProps) {
    const [requestCallback, { isLoading }] = useRequestCallbackMutation();

    const [name, setName] = useState('');
    const [email, setEmail] = useState('');
    const [phoneNumber, setPhoneNumber] = useState('');
    const [error, setError] = useState('');
    const [done, setDone] = useState(false);

    const handleSubmit = async (event: React.FormEvent) => {
        event.preventDefault();
        setError('');

        const trimmedName = name.trim();
        const trimmedEmail = email.trim();
        const trimmedPhone = phoneNumber.replace(/[\s-]/g, '');

        if (trimmedName.length < 2) return setError('Please enter your name.');
        if (!EMAIL_PATTERN.test(trimmedEmail)) return setError('Please enter a valid email address.');
        if (!PHONE_PATTERN.test(trimmedPhone)) return setError('Please enter a valid phone number.');

        try {
            await requestCallback({
                name: trimmedName,
                email: trimmedEmail,
                phoneNumber: trimmedPhone,
                source: 'intelligence-one-pager',
                ...(selectedPlanId ? { planId: selectedPlanId } : {}),
            }).unwrap();
            setDone(true);
            // No track() here: the backend emits `callback:requested` from
            // CallbacksService.create, so it fires once and can't be forged.
        } catch (err) {
            setError(parseErrorMessage(err, "We couldn't submit that. Please try again."));
        }
    };

    if (done) {
        return (
            <section className="flex flex-col items-center justify-center gap-2 rounded-2xl border border-[#FF6B35]/30 bg-[#FFF3EE] px-5 py-6 text-center">
                <CheckCircle2 className="h-8 w-8 text-[#FF6B35]" aria-hidden="true" />
                <h3 className="text-base font-semibold text-[#15171C]">We'll call you shortly</h3>
                <p className="max-w-sm text-[13.5px] leading-snug text-[#6B6862]">
                    Your request is in. One of our intelligence leads will reach out on {phoneNumber} to scope your
                    check.
                </p>
            </section>
        );
    }

    return (
        <form onSubmit={handleSubmit} className="flex flex-col gap-2.5">
            <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Name"
                autoComplete="name"
                aria-label="Full name"
                className={FIELD_CLASS}
            />
            <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Email"
                autoComplete="email"
                aria-label="Work email"
                className={FIELD_CLASS}
            />
            <input
                type="tel"
                value={phoneNumber}
                onChange={(e) => setPhoneNumber(e.target.value)}
                placeholder="Phone Number"
                autoComplete="tel"
                aria-label="Phone number"
                className={FIELD_CLASS}
            />

            {error && (
                <p role="alert" className="text-[12.5px] leading-snug text-red-600">
                    {error}
                </p>
            )}

            <button
                type="submit"
                disabled={isLoading}
                className="group mt-0.5 inline-flex items-center justify-center gap-2 rounded-xl bg-[#FF6B35] px-5 py-3 text-[14px] font-semibold text-white shadow-sm transition-all hover:bg-[#F05A22] disabled:opacity-60"
            >
                {isLoading ? (
                    <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
                ) : (
                    <PhoneCall className="h-4 w-4" aria-hidden="true" />
                )}
                {isLoading ? 'Sending…' : 'Call me back'}
                {!isLoading && (
                    <ArrowRight
                        className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5"
                        aria-hidden="true"
                    />
                )}
            </button>
        </form>
    );
}
